/**
 * 语音翻译会话 API
 */

import { request } from '@/shared/api/request'
import { getCorrectedApiBaseUrl } from '@/shared/api/api-base'
import {
  publicFileAccessUrl,
  fileProxyUrl,
} from '@/shared/utils/file-url'

export { publicFileAccessUrl } from '@/shared/utils/file-url'

export interface SourceLanguage {
  languageCode: string
  languageName?: string
  languageEnName?: string
}

export interface TargetLanguage {
  languageCode: string
  languageName?: string
  languageEnName?: string
}

export interface SessionCreateRequest {
  sourceLanguage: SourceLanguage
  targetLanguages: TargetLanguage[]
}

export interface SessionCreateResponse {
  appId: string
  userCode: string
  shareLink: string
}

export interface ShareLinkResponse {
  userCode: string
  shareLink: string
}

/**
 * 创建会话
 */
export function createSession(data: SessionCreateRequest) {
  return request<SessionCreateResponse>({
    url: '/speech-translation/sessions',
    method: 'post',
    data
  })
}

/**
 * 获取会话详情
 */
export function getSession(appId: string) {
  return request({
    url: `/speech-translation/sessions/${appId}`,
    method: 'get'
  })
}

/**
 * 结束会话
 */
export function endSession(appId: string, durationSeconds = 0) {
  return request({
    url: `/speech-translation/sessions/${appId}/end`,
    method: 'put',
    params: { duration_seconds: durationSeconds }
  })
}

export interface SubtitleStyle {
  backgroundColor: string
  opacity: number
  fontColor: string
  fontSize: number
  fontFamily: string
  lineSpacing?: number
  backgroundImagePC?: string
  backgroundImageH5?: string
}

/** 观众端背景图上传（存储于 temp 分类，不设过期），返回可公开访问的 URL */
export interface FileUploadResponse {
  success: boolean
  message: string
  file: { id: number; access_url?: string; file_key?: string }
}

export function uploadBackgroundImage(file: File): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/files/upload',
    method: 'post',
    params: {
      file_category: 'temp',
      // 仍为 temp 目录；is_temporary=false 且不传 expires_in_hours → 后端 expires_at=null，不按过期拒绝下载
      is_temporary: false
    },
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' }
  }) as Promise<FileUploadResponse>
}

/**
 * 根据上传结果得到观众端可用的背景图 URL。
 * 本地存储时 access_url 常为 /temp/...，在仅反代 /api 的环境下须转为 /api/v1/temp/...，否则浏览器会得到 SPA 的 index.html。
 */
export function getBackgroundImageUrl(res: FileUploadResponse): string {
  const file = res?.file
  const raw = file?.access_url?.trim()
  if (raw) {
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) {
      return publicFileAccessUrl(raw)
    }
    if (raw.startsWith('/')) {
      return publicFileAccessUrl(raw)
    }
  }
  const key = file?.file_key?.trim()
  if (key && !key.includes('..')) {
    const p = key.startsWith('/') ? key : `/${key}`
    return publicFileAccessUrl(p)
  }
  const id = file?.id
  if (id != null) {
    return fileProxyUrl(id, getCorrectedApiBaseUrl())
  }
  return ''
}

export interface SubtitlePresetResponse extends SubtitleStyle {
  liveSynced?: boolean
  liveSyncFailed?: boolean
  syncedAppId?: string | null
}

/** 读取用户字幕预设（无记录时后端返回系统 DEFAULT） */
export function getSubtitlePreset(): Promise<SubtitleStyle> {
  return request({
    url: '/speech-translation/subtitle-preset',
    method: 'get'
  }) as Promise<SubtitleStyle>
}

/**
 * 保存用户字幕预设；有进行中会议时后端同步 Redis + SSE
 */
export function putSubtitlePreset(style: SubtitleStyle): Promise<SubtitlePresetResponse> {
  return request({
    url: '/speech-translation/subtitle-preset',
    method: 'put',
    data: style
  }) as Promise<SubtitlePresetResponse>
}

/**
 * 会议中切换语言
 */
export function updateSessionLanguages(
  appId: string,
  data: { sourceLanguage?: SourceLanguage; targetLanguages?: TargetLanguage[] }
) {
  return request({
    url: `/speech-translation/sessions/${appId}/languages`,
    method: 'put',
    data
  })
}

/**
 * 会议中换语言前：保存当前片段为段落并清空现场片段（无片段时仅清空）
 */
export function saveSnapshot(appId: string) {
  return request<{ success: boolean; message?: string }>({
    url: `/speech-translation/sessions/${appId}/snapshot`,
    method: 'post'
  })
}

/**
 * 追加翻译片段
 */
export function appendFragment(appId: string, data: { sourceText: string; translations: Record<string, string> }) {
  return request({
    url: `/speech-translation/sessions/${appId}/fragments`,
    method: 'post',
    data
  })
}

/** 识别中的临时译文推给观众端（不入片段）；源文与译文均为空时清空观众端预览 */
export function publishPreview(appId: string, data: { sourceText: string; translations: Record<string, string> }) {
  return request<{ success: boolean }>({
    url: `/speech-translation/sessions/${appId}/preview`,
    method: 'post',
    data
  })
}

/**
 * 上报时长
 */
export function heartbeat(appId: string, seconds: number) {
  return request({
    url: '/speech-translation/sessions/heartbeat',
    method: 'post',
    params: { app_id: appId, seconds }
  })
}

/**
 * 获取用户固定观看链接
 */
export function getShareLink() {
  return request<ShareLinkResponse>({
    url: '/speech-translation/user/share-link',
    method: 'get'
  })
}

/** 与后端 get_history 返回字段一致；status 0-未完成 1-已完成 2-失败 */
export interface HistoryItem {
  id: string
  app_id?: string
  spoken_language: string
  duration: number
  content: string
  target_count: number
  target_languages: string
  status: number
  time_create: string | null
  error_info: string | null
}

export interface HistoryResponse {
  items: HistoryItem[]
  total: number
}

/**
 * 获取历史会话列表；可按开始时间范围筛选（time_start/time_end 传 ISO 或 YYYY-MM-DD）
 */
export function getHistory(params?: {
  limit?: number
  offset?: number
  time_start?: string
  time_end?: string
}) {
  return request<HistoryResponse>({
    url: '/speech-translation/sessions/history',
    method: 'get',
    params
  })
}

/**
 * 导出单条记录为 txt 文件（文件名 {workId}.txt）
 * 返回 Blob，由调用方触发下载并指定文件名
 */
export function exportRecord(workId: string): Promise<Blob> {
  return request({
    url: `/speech-translation/works/${workId}/export`,
    method: 'get',
    responseType: 'blob'
  }).then((res: { data: Blob }) => res.data)
}

/** 语音服务 token 接口返回，region 与后端 MICROSOFT_SPEECH_REGION 一致 */
export interface SpeechTokenResponse {
  token: string
  region: string
}

/**
 * 获取 Azure Speech token 与 region（供前端 SDK 直连使用）
 * region 用于连接 wss://{region}.stt.speech.microsoft.com，须与签发 token 的区一致
 */
export function getSpeechToken(): Promise<SpeechTokenResponse> {
  return request({
    url: '/speech-translation/token',
    method: 'get'
  }) as Promise<SpeechTokenResponse>
}
